import { faChevronUp as ExpandIcon } from '@fortawesome/pro-light-svg-icons';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { merge, startCase, uniq } from 'lodash';
import { useMemo } from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router';

import AssetPanelItemWrapper from '@/components/AssetPanelItemWrapper/AssetPanelItemWrapper';
import CheckPermissions from '@/components/CheckPermissions';
import KeyValueResponsive from '@/components/KeyValueResponsive';
import type { KeyValueResponsiveModel } from '@/components/KeyValueResponsive/KeyValueResponsive.types';
import PERMISSIONS from '@/constants/auth.constants';
import { MARKDOWN_REMARK_PLUGINS } from '@/constants/markdown.constants';
import { useGridTheme } from '@/hooks/useGridTheme';
import Panel from '@/layout/Panel/Panel';
import {
  ALERT_RULE_MAIN_DETAILS,
  ALERT_RULE_METRICS_COLUMNS
} from '@/pages/AlertRules/AlertRules.constants';
import {
  selectedAlertRuleAtom,
  useAlertRuleSelection
} from '@/pages/AlertRules/AlertRules.store';
import type {
  AlertRuleOrgInstructionsDTO,
  AlertRuleOrgMemoryDTO
} from '@/pages/AlertRules/AlertRules.types';
import {
  organizationsAtom,
  selectedOrganizationIdAtom
} from '@/pages/Organizations';

import useStyles from './AlertRulePanel.styles';
import type { AlertRulePanelType } from './AlertRulePanel.types';
import { formatAlertRuleDetailsValue } from './AlertRulePanel.utils';

const AlertRulePanel = (props: AlertRulePanelType) => {
  const { setOpen } = props;
  const { classes } = useStyles();
  const { deselectAlertRule } = useAlertRuleSelection();
  const handleClose = () => {
    deselectAlertRule();
  };
  const [selectedAlertRule] = useAtom(selectedAlertRuleAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [selectedOrgId] = useAtom(selectedOrganizationIdAtom);
  const gridTheme = useGridTheme();

  const panelActions = useMemo(
    () => (
      <>
        <CheckPermissions permissions={[PERMISSIONS.CASES_UPDATE]}>
          <Button
            onClick={() => setOpen(true)}
            sx={{ mr: 1 }}
            variant="outlined"
          >
            Edit
          </Button>
        </CheckPermissions>
        <CheckPermissions permissions={[PERMISSIONS.LOGS_READ]}>
          <Button
            variant="outlined"
            href={`https://events.pondurance.com/${selectedAlertRule?.repo}/triggers?triggerId=${selectedAlertRule?.trigger_id}&search=${encodeURIComponent(selectedAlertRule?.name || '')}`}
            target="_blank"
          >
            <FontAwesomeIcon icon={faExternalLink} size="sm" />
          </Button>
        </CheckPermissions>
      </>
    ),
    [selectedAlertRule?.trigger_id]
  );

  const alertRuleDetails = Object.entries(ALERT_RULE_MAIN_DETAILS)
    .filter(([key]) => {
      const value = selectedAlertRule?.[key as keyof typeof selectedAlertRule];
      return value !== null && value !== undefined;
    })
    .map(([key, label]) => ({
      label,
      value: formatAlertRuleDetailsValue(
        selectedAlertRule?.[key as keyof typeof selectedAlertRule] as
          | string
          | boolean
      )
    })) as KeyValueResponsiveModel[];

  const alertRuleSampleAlerts = useMemo(
    () =>
      selectedAlertRule?.case_examples
        ?.filter((item) => item.organization_id === selectedOrgId)
        .map((example) => ({
          label: startCase(example.prompt_verdict),
          value: (
            <Link
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
              to={`/case/${example.soc_id}`}
            >
              {example.soc_case_title} - {example.soc_id}
            </Link>
          )
        })) as KeyValueResponsiveModel[],
    [selectedAlertRule?.case_examples, selectedOrgId]
  );

  const showOrgRules =
    selectedOrgId === 0 ||
    selectedAlertRule?.organization_instructions?.some(
      (orgInst) => orgInst.organization_id === selectedOrgId
    );

  const selectedOrgInstructions = useMemo(
    () =>
      selectedAlertRule?.organization_instructions?.filter(
        (orgInst) => orgInst.organization_id === selectedOrgId
      ),
    [selectedAlertRule?.organization_instructions, selectedOrgId]
  );

  const orgInstructionsOrgList = useMemo(
    () =>
      selectedAlertRule?.organization_instructions?.map(
        (orgInst) =>
          organizations.find((org) => org.id === orgInst.organization_id)?.name
      ) as string[],
    [selectedAlertRule?.organization_instructions, organizations]
  );

  const selectedOrgMemory = useMemo(
    () =>
      selectedAlertRule?.organization_memory?.filter(
        (orgMem) => orgMem.organization_id === selectedOrgId
      ),
    [selectedAlertRule?.organization_memory, selectedOrgId]
  );

  const orgMemoryOrgList = useMemo(
    () =>
      selectedAlertRule?.organization_memory?.map(
        (orgMem) =>
          organizations.find((org) => org.id === orgMem.organization_id)?.name
      ) as string[],
    [selectedAlertRule?.organization_memory, organizations]
  );

  const orgExamplesOrgList = uniq(
    selectedAlertRule?.case_examples?.map(
      (example) =>
        organizations.find((org) => org.id === example.organization_id)?.name
    )
  ) as string[];

  const ruleMetrics: any = merge(
    {},
    selectedAlertRule?.stats,
    selectedAlertRule?.stats?._benign_breakdown,
    selectedAlertRule?.stats?._counts_breakdown,
    selectedAlertRule?.stats?._fp_breakdown
  );

  const metricsTableTheme = gridTheme.withParams({
    wrapperBorderRadius: 0,
    wrapperBorder: true
  });

  const aiTotal =
    (ruleMetrics?.case_verdicts_tp?.tp_ai || 0) +
    (ruleMetrics?.case_verdicts_fp?.fp_ai || 0);

  const socTotal =
    (ruleMetrics?.case_verdicts_tp?.tp_soc || 0) +
    (ruleMetrics?.case_verdicts_fp?.fp_soc || 0) +
    (ruleMetrics?.case_verdicts_benign?.benign_soc || 0);

  const scopeTotal =
    (ruleMetrics?.case_verdicts_tp?.tp_scope || 0) +
    (ruleMetrics?.case_verdicts_fp?.fp_scope || 0) +
    (ruleMetrics?.case_verdicts_benign?.benign_scope || 0);

  const ruleMetricsData = [
    {
      field: 'SOC',
      total: socTotal,
      tp: ruleMetrics?.case_verdicts_tp?.tp_soc || 0,
      fp: ruleMetrics?.case_verdicts_fp?.fp_soc || 0,
      benign: ruleMetrics?.case_verdicts_benign?.benign_soc || 0
    },
    {
      field: 'AI',
      total: aiTotal,
      tp: ruleMetrics?.case_verdicts_tp?.tp_ai || 0,
      fp: ruleMetrics?.case_verdicts_fp?.fp_ai || 0,
      benign: '--'
    },
    {
      field: 'Scope',
      total: scopeTotal,
      tp: ruleMetrics?.case_verdicts_tp?.tp_scope || 0,
      fp: ruleMetrics?.case_verdicts_fp?.fp_scope || 0,
      benign: ruleMetrics?.case_verdicts_benign?.benign_scope || 0
    }
  ];

  return (
    <Panel
      action={panelActions}
      title="Alert Rule Details"
      onClose={handleClose}
    >
      <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
        <AccordionSummary
          className={classes.alertRulePanelAccordionSummary}
          expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
          id="panel-details-header"
        >
          Details
        </AccordionSummary>
        <AccordionDetails className={classes.alertRuleAccordionDetails}>
          <KeyValueResponsive items={alertRuleDetails} />
          <AssetPanelItemWrapper title="Description">
            <Box className={classes.alertRulesMarkdown}>
              <Markdown
                disallowedElements={['img', 'a']}
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                unwrapDisallowed
              >
                {/* Regex formats the description to add in non-breaking spaces after newlines, makes text more readable */}
                {selectedAlertRule?.description?.replace(/\n/g, '\n &nbsp;')}
              </Markdown>
            </Box>
          </AssetPanelItemWrapper>
        </AccordionDetails>
      </Accordion>
      <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
        <AccordionSummary
          className={classes.alertRulePanelAccordionSummary}
          expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
          id="panel-ticket-template-header"
        >
          Ticket Template
        </AccordionSummary>
        <AccordionDetails className={classes.alertRuleAccordionDetails}>
          <AssetPanelItemWrapper title="Template Name">
            {selectedAlertRule?.ticket_template?.name || '--'}
          </AssetPanelItemWrapper>
          <AssetPanelItemWrapper title="Summary">
            <Box className={`${classes.alertRulesMarkdown} markdown-body`}>
              <Markdown
                disallowedElements={['img', 'a']}
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                unwrapDisallowed
              >
                {selectedAlertRule?.ticket_template?.summary || '--'}
              </Markdown>
            </Box>
          </AssetPanelItemWrapper>
        </AccordionDetails>
      </Accordion>
      <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
        <AccordionSummary
          className={classes.alertRulePanelAccordionSummary}
          expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
          id="panel-global-instructions-header"
        >
          Global Instructions for Rule
        </AccordionSummary>
        <AccordionDetails className={classes.alertRuleAccordionDetails}>
          <Box className="markdown-body">
            <Markdown
              disallowedElements={['img', 'a']}
              remarkPlugins={MARKDOWN_REMARK_PLUGINS}
              unwrapDisallowed
            >
              {selectedAlertRule?.instructions
                ? selectedAlertRule?.instructions
                : '--'}
            </Markdown>
          </Box>
        </AccordionDetails>
      </Accordion>
      {selectedOrgId !== 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-organization-instructions-header"
          >
            Organization Instructions
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {showOrgRules
              ? selectedOrgInstructions?.map(
                  (orgInst: AlertRuleOrgInstructionsDTO) => (
                    <Box
                      key={orgInst.organization_id}
                      className="markdown-body"
                    >
                      <Markdown
                        disallowedElements={['img', 'a']}
                        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                        unwrapDisallowed
                      >
                        {orgInst.instructions ? orgInst.instructions : '--'}
                      </Markdown>
                    </Box>
                  )
                )
              : '--'}
          </AccordionDetails>
        </Accordion>
      )}
      {selectedOrgId === 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-organization-instructions-header"
          >
            Organizations With Instructions
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {orgInstructionsOrgList?.length
              ? orgInstructionsOrgList?.map((orgInst: string) => (
                  <Typography style={{ padding: '0.5rem 0' }} key={orgInst}>
                    {orgInst}
                  </Typography>
                ))
              : '--'}
          </AccordionDetails>
        </Accordion>
      )}
      {selectedOrgId !== 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-org-memory-header"
          >
            Organization Memory
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {selectedOrgMemory?.length
              ? selectedOrgMemory?.map((orgMem: AlertRuleOrgMemoryDTO) => (
                  <Box
                    key={`${orgMem.organization_id}-${orgMem.memory}`}
                    className="markdown-body"
                  >
                    <Markdown
                      disallowedElements={['img', 'a']}
                      remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                      unwrapDisallowed
                    >
                      {orgMem.memory ? orgMem.memory : '--'}
                    </Markdown>
                  </Box>
                ))
              : '--'}
          </AccordionDetails>
        </Accordion>
      )}
      {selectedOrgId === 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-orgs-with-memory-header"
          >
            Organizations With Memory
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {orgMemoryOrgList?.length
              ? orgMemoryOrgList?.map((orgName: string) => (
                  <Typography style={{ padding: '0.5rem 0' }} key={orgName}>
                    {orgName}
                  </Typography>
                ))
              : '--'}
          </AccordionDetails>
        </Accordion>
      )}

      {selectedOrgId !== 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-sample-alerts-header"
          >
            Sample Cases
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {alertRuleSampleAlerts?.length ? (
              <KeyValueResponsive resizable items={alertRuleSampleAlerts} />
            ) : (
              '--'
            )}
          </AccordionDetails>
        </Accordion>
      )}
      {selectedOrgId === 0 && (
        <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
          <AccordionSummary
            className={classes.alertRulePanelAccordionSummary}
            expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
            id="panel-organization-sample-cases-header"
          >
            Organizations With Sample Cases
          </AccordionSummary>
          <AccordionDetails className={classes.alertRuleAccordionDetails}>
            {orgExamplesOrgList?.length
              ? orgExamplesOrgList?.map((orgInst: string) => (
                  <Typography style={{ padding: '0.5rem 0' }} key={orgInst}>
                    {orgInst}
                  </Typography>
                ))
              : '--'}
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion className={classes.alertRulePanelAccordion} defaultExpanded>
        <AccordionSummary
          className={classes.alertRulePanelAccordionSummary}
          expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
          id="panel-metrics-header"
        >
          Metrics
        </AccordionSummary>
        <AccordionDetails>
          <AgGridReact
            columnDefs={ALERT_RULE_METRICS_COLUMNS}
            containerStyle={{
              maxHeight: '300px',
              overflow: 'auto'
            }}
            defaultColDef={{ suppressHeaderMenuButton: true }}
            domLayout="autoHeight"
            headerHeight={32}
            loading={false}
            overlayNoRowsTemplate='<div style="display: flex; align-items: center; justify-content: center; height: 100%;">No metrics available</div>'
            rowData={ruleMetricsData}
            rowHeight={32}
            suppressCellFocus
            suppressContextMenu
            theme={metricsTableTheme}
          />
        </AccordionDetails>
      </Accordion>
    </Panel>
  );
};

export default AlertRulePanel;
